import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { PHONE_DISPLAY, PHONE_LINK } from "../data/site";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Why MODORS", to: "/#why-modors" },
  { label: "Technology", to: "/#technology" },
  { label: "Applications", to: "/#applications" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-40 bg-brand-coal/95 backdrop-blur-md border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[13px] font-medium tracking-[0.14em] uppercase text-white/70 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <a
            href={PHONE_LINK}
            data-testid="nav-phone-link"
            className="hidden xl:block text-[13px] font-semibold tracking-wider text-white/80 hover:text-brand-red transition-colors duration-300"
          >
            {PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            data-testid="nav-get-quote-btn"
            className="hidden sm:inline-flex group items-center gap-2 bg-brand-red text-white text-[13px] font-semibold tracking-[0.14em] uppercase px-5 py-3 hover:bg-white hover:text-brand-ink transition-colors duration-300"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-brand-coal border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 font-display text-2xl uppercase tracking-wide text-white/85 hover:text-brand-red transition-colors duration-300 border-b border-white/5"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-get-quote-btn"
                className="mt-5 inline-flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-semibold tracking-[0.14em] uppercase px-6 py-4"
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
